/**
 * Command Center -- container with tab navigation for Today, Tasks, Calendar panels.
 *
 * Desktop (md+): main content area, flex-1, full height.
 * Mobile (<md): main content area (always visible, no slide-out).
 */

import { useState } from "react";
import type { SidebarTask, SidebarEvent, TodayPlan, UserProfile } from "./types";
import { TodayPanel } from "./today-panel";
import { CalendarPanel } from "./calendar-panel";
import { TasksPanel } from "./tasks-panel";

interface SidebarProps {
  tasks: SidebarTask[];
  events: SidebarEvent[];
  todayPlan?: TodayPlan;
  projects?: Array<{ id: string; name: string }>;
  profile?: UserProfile | null;
  onTaskClick: (task: SidebarTask) => void;
  onEventClick: (event: SidebarEvent) => void;
  onCalendarRangeChange?: (start: string, end: string) => void;
  onOpenChat?: () => void;
  onOpenWizard?: () => void;
  // Task mutations
  onCreateTask?: (input: { title: string; priority?: string; dueDate?: string; consequenceOfDelay?: string }) => void;
  onUpdateTask?: (id: string, updates: Record<string, unknown>) => void;
  onDeleteTask?: (id: string) => void;
}

type TabId = "today" | "tasks" | "calendar";

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: "today", label: "Today" },
  { id: "tasks", label: "Tasks" },
  { id: "calendar", label: "Calendar" },
];

export function Sidebar({
  tasks,
  events,
  todayPlan,
  projects,
  profile,
  onTaskClick,
  onEventClick,
  onCalendarRangeChange,
  onOpenChat,
  onOpenWizard,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
}: SidebarProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<TabId>("today");

  return (
    <aside
      className={[
        "bg-bg-secondary flex flex-col",
        // Mobile: main content area, full width
        "flex-1 h-full",
        // Desktop: main content area, flex-1
        "md:flex-1 md:h-full md:w-auto",
      ].join(" ")}
    >
      {/* Tab navigation */}
      <div className="flex border-b border-border px-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={[
              "text-xs font-medium px-3 py-2 transition-colors duration-100",
              activeTab === tab.id
                ? "border-b-2 border-primary text-text-primary"
                : "text-text-tertiary hover:text-text-secondary",
            ].join(" ")}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {activeTab === "today" && (
          <TodayPanel
            tasks={tasks}
            events={events}
            todayPlan={todayPlan}
            profile={profile}
            onTaskClick={onTaskClick}
            onEventClick={onEventClick}
            onOpenChat={onOpenChat}
            onOpenWizard={onOpenWizard}
          />
        )}
        {activeTab === "tasks" && (
          <TasksPanel
            tasks={tasks}
            projects={projects}
            onTaskClick={onTaskClick}
            onCreateTask={onCreateTask ?? (() => {})}
            onUpdateTask={onUpdateTask ?? (() => {})}
            onDeleteTask={onDeleteTask ?? (() => {})}
          />
        )}
        {activeTab === "calendar" && (
          <CalendarPanel
            tasks={tasks}
            events={events}
            onTaskClick={onTaskClick}
            onEventClick={onEventClick}
            onDateRangeChange={onCalendarRangeChange}
          />
        )}
      </div>
    </aside>
  );
}
